import { ReactNode, useState } from 'react';
import {
  Content,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { MTableToolbar } from '@material-table/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PublishIcon from '@material-ui/icons/Publish';
import RefreshIcon from '@material-ui/icons/Refresh';
import RestoreIcon from '@material-ui/icons/Restore';
import {
  ServiceDeployments,
  componentsOf,
  parseImageTag,
  PROMOTABLE_ENVIRONMENTS,
  environmentLabel,
  promotableServices,
  releasePromotePermission,
  releaseRollbackPermission,
  systemMembers,
  unitFor,
} from '@internal/backstage-plugin-release-common';
import { PendingPromotions, ReleaseLineSummary } from './PendingPromotions';
import { ReleaseAction, ReleaseDialog } from './ReleaseDialog';
import { useFleetDeployments } from './useFleetDeployments';
import { useReleaseStates } from './useReleaseStates';
import { useStartRelease } from './useStartRelease';


interface Row {
  service: string;
  /** Environment name to the versions running there. Keyed rather than three
   * fixed fields: not every app has the same environments — the platform's own
   * tooling deploys to `mgmt` and never sees staging. */
  versions: Record<string, string>;
  rebuilds: boolean;
  deployments: ServiceDeployments;
}

/** Environments a release can be sent to, among those the listed components
 * actually have. dev is left out: it is continuous, owned by the image
 * updater, and never a target — a column for it invites someone to try. */
function environmentsOf(services: ServiceDeployments[]): string[] {
  const present = new Set(
    services.flatMap(service => service.units.map(unit => unit.environment)),
  );
  return PROMOTABLE_ENVIRONMENTS.filter(environment => present.has(environment));
}

/**
 * Says why a control is dead, rather than leaving it inert with no explanation.
 * A disabled MUI button swallows pointer events, so the tooltip hangs off a
 * wrapping span instead of the button itself.
 */
/** MUI shouts button labels by default. These are ordinary verbs in a table,
 * not calls to action, and read better spoken than announced. */
const SENTENCE_CASE = { textTransform: 'none' } as const;

const WhenDenied = ({
  allowed,
  children,
}: {
  allowed: boolean;
  children: ReactNode;
}) =>
  allowed ? (
    <>{children}</>
  ) : (
    <Tooltip title="You do not have permission to release this system.">
      <span>{children}</span>
    </Tooltip>
  );

function versionsFor(service: ServiceDeployments, environment: string): string {
  const versions = new Set(
    componentsOf(service)
      .map(component => unitFor(service, environment, component))
      .filter((unit): unit is NonNullable<typeof unit> => Boolean(unit))
      .map(unit => parseImageTag(unit.imageTag)?.version ?? unit.imageTag),
  );
  return versions.size === 0 ? '—' : [...versions].join(' / ');
}

/**
 * The manipulation surface: every component of the system, what it runs, and
 * the two actions — one at a time or several at once.
 *
 * Confirming does not write from here. It starts a scaffolder task, which is
 * what pushes tags, so every release carries the same audit trail whether it
 * came from this page or from the template directly.
 */
export const FleetReleasesPage = () => {
  const { entity } = useEntity();
  const state = useFleetDeployments({ system: entity.metadata.name });

  const [action, setAction] = useState<ReleaseAction | null>(null);
  const [chosen, setChosen] = useState<ServiceDeployments[]>([]);
  const [selected, setSelected] = useState<ServiceDeployments[]>([]);

  // Courtesy, not control — the gate is on the scaffolder action. `allowed` is
  // false while the check is in flight, so a control never flashes enabled
  // before turning out to be denied.
  const canPromote = usePermission({
    permission: releasePromotePermission,
  }).allowed;
  const canRollback = usePermission({
    permission: releaseRollbackPermission,
  }).allowed;

  const close = () => {
    setAction(null);
    setChosen([]);
  };

  const onConfirm = useStartRelease(close);

  // Scope to this system's own members. The backend reports every overlay in
  // gitops-platform, so without this the page lists the legacy fleet too.
  const services =
    state.kind === 'loaded'
      ? promotableServices(state.response, systemMembers(entity.relations))
      : [];
  // What is in flight — the open release line and the gitops pull requests
  // not merged yet. Asked here, before the early returns, because it is a
  // hook; it stays idle until the deployments have loaded.
  const releases = useReleaseStates(
    services.map(service => service.service),
    state.generation,
  );
  const inFlight = releases.kind === 'loaded' ? releases.byService : {};

  if (state.kind === 'loading') {
    return (
      <Content>
        <Progress />
      </Content>
    );
  }

  if (state.kind === 'error') {
    return (
      <Content>
        <ResponseErrorPanel error={state.error} />
      </Content>
    );
  }

  const environments = environmentsOf(services);
  const rows: Row[] = services.map(service => ({
    service: service.service,
    versions: Object.fromEntries(
      environments.map(environment => [
        environment,
        versionsFor(service, environment),
      ]),
    ),
    rebuilds: service.rebuildsPerEnvironment,
    deployments: service,
  }));

  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggle = (service: ServiceDeployments) =>
    setSelected(current =>
      current.some(entry => entry.service === service.service)
        ? current.filter(entry => entry.service !== service.service)
        : [...current, service],
    );

  const columns: TableColumn<Row>[] = [
    {
      // Our own checkbox column rather than material-table's `selection`: its
      // select-all header cell and the row cells sit on different paddings, so
      // the column reads as misaligned and no option fixes it. Two ordinary
      // cells line up by construction.
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={selected.length > 0 && !allSelected}
          onChange={() =>
            setSelected(allSelected ? [] : rows.map(row => row.deployments))
          }
          inputProps={{ 'aria-label': 'Select all components' }}
        />
      ) as unknown as string,
      field: 'service',
      sorting: false,
      width: '48px',
      render: row => (
        <Checkbox
          checked={selected.some(entry => entry.service === row.service)}
          onChange={() => toggle(row.deployments)}
          inputProps={{ 'aria-label': `Select ${row.service}` }}
        />
      ),
    },
    { title: 'Component', field: 'service' },
    ...environments.map(environment => ({
      title: environmentLabel(environment),
      field: `versions.${environment}`,
      render: (row: Row) => (
        <>
          {row.versions[environment]}
          {row.rebuilds && environment === 'prod' && (
            <Typography variant="body2" color="textSecondary" component="span">
              {'  '}rebuilt
            </Typography>
          )}
          <PendingPromotions
            pending={inFlight[row.service]?.pending ?? []}
            environment={environment}
          />
        </>
      ),
    })),
    {
      // The line a release is being cut on, so a candidate that is tagged but
      // not deployed anywhere yet still shows up somewhere.
      title: 'Release line',
      field: 'service',
      sorting: false,
      render: (row: Row) => (
        <ReleaseLineSummary line={inFlight[row.service]?.line} />
      ),
    },
    {
      title: '',
      field: 'service',
      sorting: false,
      render: row => (
        <Box display="flex" gridGap={8} justifyContent="flex-end">
          <WhenDenied allowed={canPromote}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<PublishIcon fontSize="small" />}
              style={SENTENCE_CASE}
              disabled={!canPromote}
              onClick={() => {
                setChosen([row.deployments]);
                setAction('promote');
              }}
            >
              Promote
            </Button>
          </WhenDenied>
          <WhenDenied allowed={canRollback}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestoreIcon fontSize="small" />}
              style={SENTENCE_CASE}
              disabled={!canRollback}
              onClick={() => {
                setChosen([row.deployments]);
                setAction('rollback');
              }}
            >
              Rollback
            </Button>
          </WhenDenied>
        </Box>
      ),
    },
  ];


  return (
    <Content>
      {/* No InfoCard around this: Backstage's Table renders its own surface
          with a toolbar and title, and wrapping it in a card nests two of
          them. */}
      <Table
        title={
          <Box display="flex" alignItems="center" gridGap={4}>
            Releases
            {/* Beside the title, not beside the action: it re-reads the whole
                table, and next to "Promote selected" it reads as part of
                promoting. */}
            <Tooltip
              title={
                state.cooling
                  ? 'Just read — try again in a moment'
                  : 'Read again, past the cache'
              }
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="Refresh deployments"
                  disabled={state.cooling}
                  onClick={state.refresh}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        }
        options={{
          search: true,
          paging: false,
          toolbar: true,
          padding: 'dense',
        }}
        columns={columns}
        data={rows}
        components={{
          // A real button in the table's own toolbar, beside the filter.
          // material-table's `actions` renders whatever it is given inside an
          // IconButton, so a labelled action comes out as oversized bare text
          // — overriding the toolbar is the way to place a proper control.
          Toolbar: toolbarProps => (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flexGrow={1}>
                <MTableToolbar {...toolbarProps} />
              </Box>
              <Box pr={2} flexShrink={0}>
                <WhenDenied allowed={canPromote}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PublishIcon fontSize="small" />}
                    style={SENTENCE_CASE}
                    disabled={!canPromote || selected.length === 0}
                    onClick={() => {
                      setChosen(selected);
                      setAction('promote');
                    }}
                  >
                    Promote selected
                    {selected.length > 0 && ` (${selected.length})`}
                  </Button>
                </WhenDenied>
              </Box>
            </Box>
          ),
        }}
      />

      {/* Footnote rather than a subtitle: it explains how the numbers above
          are arrived at, which is worth saying once and only matters after
          you have read the table. */}
      <Box pt={1}>
        <Typography variant="caption" color="textSecondary" component="p">
          {rows.length} component{rows.length === 1 ? '' : 's'}. Each derives
          its own next version from its own tags.
        </Typography>
      </Box>

      <ReleaseDialog
        action={action}
        services={chosen}
        generation={state.generation}
        onClose={close}
        onConfirm={onConfirm}
      />
    </Content>
  );
};
